/**
 * After the brief: the handoff (spec 026 D-10). Shown once per story per
 * page session in the studio's preview column, after the turn that answered
 * a pending brief applied with a map that lists at least one picture: what
 * the draft needs, grouped by character with Backgrounds last, and the
 * choice between filling pictures now and going on with placeholders.
 *
 * Under a scene picture owner (spec 029 D-13, spec 030) it shows the
 * standalone scene's own map and its one scene, says "scene" for "story",
 * and is remembered under `scene:<id>` (scene and story ids collide).
 */
import React, { useContext, useMemo, useState } from 'react';
import type { PictureMap, PictureSlot, StoryApiResponse } from '../../../../Shared/src/@types';
import { isSlotFilled, isSlotNeeded, pictureMapCounts, pictureSlotLabel, pictureSlug } from '../../../../Shared/src/pictures';
import { usePictureExtensions } from './pictureExtensions';
import { PictureOwnerContext, withNoun } from './pictureOwner';
import { castGroups } from './picturesModel';
import { usePictureScenes } from './usePictureScenes';
import styles from './Pictures.module.scss';

/**
 * Stories (by id) and standalone scenes (by `scene:<id>`) whose handoff was
 * closed in this page session (in memory, as 025's fly camera).
 */
const closedHandoffs = new Set<number | string>();

export const sceneHandoffKey = (sceneId: number | string): string => `scene:${sceneId}`;
export const isHandoffClosed = (key: number | string): boolean => closedHandoffs.has(key);
export function closeHandoff(key: number | string): void {
  closedHandoffs.add(key);
}
/** Tests: a new page session. */
export function resetHandoffMemory(): void {
  closedHandoffs.clear();
}

export interface PicturesHandoffProps {
  /** The story; omitted under a scene owner, whose one scene the owner gives. */
  story?: Pick<StoryApiResponse, 'id' | 'graph' | 'storyScenes'>;
  map: PictureMap;
  /** "Fill pictures now": the Pictures view, By cast, Needed filter. */
  onFill: () => void;
  /** "Go to the studio with placeholders". */
  onClose: () => void;
}

const NO_STORY: Pick<StoryApiResponse, 'graph' | 'storyScenes'> = { graph: { nodes: [], edges: [], startNodeId: '' }, storyScenes: [] };

const neutralFirst = (slots: PictureSlot[]) => slots.find((s) => pictureSlug(s.variant) === 'neutral') ?? slots[0];

export const PicturesHandoff: React.FC<PicturesHandoffProps> = ({ story, map, onFill, onClose }) => {
  const ext = usePictureExtensions();
  const owner = useContext(PictureOwnerContext);
  const sceneOwner = owner?.noun === 'scene' ? owner : null;
  const storyScenes = usePictureScenes(sceneOwner ? NO_STORY : story ?? NO_STORY);
  const scenes = sceneOwner ? sceneOwner.scenes : storyScenes;
  const noun = sceneOwner ? 'scene' : 'story';
  const groups = useMemo(() => castGroups(map, scenes), [map, scenes]);
  const counts = pictureMapCounts(map);
  const needed = useMemo(() => groups.flatMap((g) => g.slots).filter(isSlotNeeded), [groups]);
  const [why, setWhy] = useState<PictureSlot | null>(null);

  return (
    <div className={styles.handoff} data-testid="pictures-handoff">
      <h3>Draft ready</h3>
      <p data-testid="handoff-total">{withNoun('This story needs', noun)} {counts.total} pictures</p>
      <p data-testid="handoff-counts">
        {counts.filled} from your library · {counts.needed} placeholders. {withNoun('Each one says who or what it shows, and why the story needs it.', noun)}
      </p>
      {groups.map((g) => (
        <div key={g.id} className={styles.handoffGroup} data-testid="handoff-group" data-group={g.id}>
          <span className={styles.handoffName} data-testid="handoff-group-name">{g.title}</span>
          <span className={styles.handoffWhy}>
            {g.kind === 'background' ? (sceneOwner ? 'where the scene happens' : 'where each scene happens') : neutralFirst(g.slots)?.why}
          </span>
          <div className={styles.chips}>
            {g.slots.map((s) => {
              const filled = isSlotFilled(s);
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`${styles.chip} ${filled ? styles.chipFilled : styles.chipNeeded} ${why?.id === s.id ? styles.chipOn : ''}`}
                  aria-pressed={why?.id === s.id}
                  onClick={() => setWhy(s)}
                  data-testid="handoff-chip"
                  data-slot={s.id}
                  data-state={filled ? 'filled' : 'needed'}
                >
                  {g.id === 'backgrounds' ? s.subject : s.variant}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {why && (
        <div data-testid="handoff-why">
          <p><strong>Why: {pictureSlotLabel(why)}</strong></p>
          <p>{why.why || 'No reason written yet.'}</p>
        </div>
      )}
      <div className={styles.handoffActions}>
        <button type="button" className={styles.primary} onClick={onFill} data-testid="handoff-fill">
          Fill pictures now
        </button>
        {ext.allAction?.(needed)}
        <button type="button" className={styles.button} onClick={onClose} data-testid="handoff-close">
          Go to the studio with placeholders
        </button>
      </div>
      <p className={styles.footnote}>{withNoun('The map stays in the Pictures tab and updates whenever the story changes.', noun)}</p>
    </div>
  );
};

export default PicturesHandoff;
